/**
 * The "switch" concept: Sᵢ ∈ {0,1} per WBS line - or per packet of it.
 *
 *   Work done           - read from a plant task matched to the line by WBS
 *                         code (progress 100 / status Completed; the date is
 *                         the task's actualEnd, falling back to updatedAt then
 *                         endDate).
 *   Sᵢ fires (client)   = work done + the client's approval period (repeated
 *                         once more for each resubmittal of the line). From
 *                         that day the line is billed, at its client value
 *                         (cost + the policy markup for its payout type).
 *   Client pays         = switch date + the client payment terms.
 *   Vendor/payroll pays = client-pay date + a lag that depends on HOW the
 *                         line's cost leaves the building (payoutType -
 *                         payroll / vendor / subcontractor terms, see
 *                         wbsConfig.CATEGORIES).
 *
 * Man-hour lines are cut into packets of `packetDays` across their task's work
 * window, each with its own switch: a packet's work counts as done once the
 * task's progress covers it, so payroll is billed (and covered) sooner than if
 * the whole line had to finish first.
 *
 * The vendor/payroll date is deliberately scheduled off the client-pay date,
 * not off the work date, so cash owed out is always sequenced behind cash
 * coming in - the "Cash held" figure is >= 0 by construction, not by
 * observation. The policy is stored per project + revision in the same fiveD
 * slot as the ledger (see ledger.js's file header).
 *
 * Everything here is pure except the two storage functions, matching the
 * split every other file in this folder uses (fetching lives in
 * switchApi.js). The day-by-day cash model built from the schedule lives in
 * cashTimeline.js.
 */

import { PAYOUT_TYPES } from "./wbsConfig";
import { catKeyOf, categoryOf, estCost, lineAliases } from "./buildDashboardLines";
import { resolveWbsCode } from "./procurementLink";
import { misRoot, SAMPLE_WINDOWS } from "./ledger";

// ── policy ───────────────────────────────────────────────────────────────────

export const DEFAULT_SWITCH_POLICY = {
  approvalDays: 5, // work done -> client verifies it (switch fires, line billed)
  clientLagDays: 21, // ~3 weeks: switch fired -> client invoice due
  vendorLagDays: { manhour: 7, material: 14, subcontract: 14 },
  packetDays: 14, // man-hour lines are billed in packets of this many days
  // Client value = cost x (1 + markup). Same 15% the demo ledger bills at.
  markupPct: { manhour: 15, material: 15, subcontract: 15 },
  resubmittals: {}, // line code -> times the client sent it back
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const clampDays = (value, fallback, min = 0) => {
  const n = num(value);
  return n !== null && n >= min ? Math.round(n) : fallback;
};

const clampPct = (value, fallback) => {
  const n = num(value);
  return n !== null && n >= 0 ? n : fallback;
};

const byPayoutType = (raw, defaults, clamp) =>
  Object.fromEntries(PAYOUT_TYPES.map((type) => [type, clamp(raw?.[type], defaults[type])]));

const normalizeResubmittals = (raw) =>
  Object.fromEntries(
    Object.entries(raw && typeof raw === "object" ? raw : {})
      .map(([code, count]) => [code, clampDays(count, 0)])
      .filter(([, count]) => count > 0),
  );

/** Fills in anything missing/invalid with the default, so a partial or
 * corrupt saved policy (or one saved before a field existed) never breaks the
 * schedule - it just falls back. */
export const normalizeSwitchPolicy = (raw) => ({
  approvalDays: clampDays(raw?.approvalDays, DEFAULT_SWITCH_POLICY.approvalDays),
  clientLagDays: clampDays(raw?.clientLagDays, DEFAULT_SWITCH_POLICY.clientLagDays),
  vendorLagDays: byPayoutType(raw?.vendorLagDays, DEFAULT_SWITCH_POLICY.vendorLagDays, clampDays),
  packetDays: clampDays(raw?.packetDays, DEFAULT_SWITCH_POLICY.packetDays, 1),
  markupPct: byPayoutType(raw?.markupPct, DEFAULT_SWITCH_POLICY.markupPct, clampPct),
  resubmittals: normalizeResubmittals(raw?.resubmittals),
});

/** Policy saved for this project + revision, or the default. */
export const extractSwitchPolicy = (mis, projectId, revision) =>
  normalizeSwitchPolicy(misRoot(mis)?.fiveD?.[projectId]?.[revision]?.switchPolicy);

/**
 * The MIS JSON to POST after the policy changes - merges into the same
 * per-revision object setLedgerInMis writes to, so entries already saved
 * there survive (see ledger.js's file header).
 */
export const setSwitchPolicyInMis = (existing, projectId, revision, policy) => {
  const base = existing && typeof existing === "object" ? existing : {};
  const fiveD = base.fiveD && typeof base.fiveD === "object" ? base.fiveD : {};
  const project =
    fiveD[projectId] && typeof fiveD[projectId] === "object" ? fiveD[projectId] : {};
  const revisionBlob =
    project[revision] && typeof project[revision] === "object" ? project[revision] : {};

  return {
    ...base,
    fiveD: {
      ...fiveD,
      [projectId]: { ...project, [revision]: { ...revisionBlob, switchPolicy: policy } },
    },
  };
};

// ── dates ────────────────────────────────────────────────────────────────────

export const isoDateOnly = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
};

export const addDays = (isoDate, days) => {
  const d = new Date(`${isoDate}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + Math.round(days));
  return d.toISOString().slice(0, 10);
};

export const diffDays = (fromIso, toIso) =>
  Math.round(
    (new Date(`${toIso}T00:00:00.000Z`).getTime() -
      new Date(`${fromIso}T00:00:00.000Z`).getTime()) /
      86400000,
  );

// ISO dates compare correctly as strings.
const earlierOf = (a, b) => (a <= b ? a : b);

// ── matching tasks to lines ──────────────────────────────────────────────────

const FIRED_STATUSES = new Set(["completed"]);

/** True once a task's work is done: 100% progress, or a Completed status
 * (either can be set without the other, going by what the Task List exposes). */
export const isSwitchFired = (task) =>
  (num(task?.progress) ?? 0) >= 100 ||
  FIRED_STATUSES.has(String(task?.status ?? "").trim().toLowerCase());

/** The date the work was done: the field meant for it, falling back to
 * whichever else the task actually has. */
const workDoneDateOf = (task) =>
  isoDateOnly(task?.actualEnd) ?? isoDateOnly(task?.updatedAt) ?? isoDateOnly(task?.endDate);

/**
 * One plant task per line, matched by WBS code the same way procurement lines
 * are (see procurementLink.js's resolveWbsCode) - tasks are plant-wide, like
 * MRQs, so the same tag-scoped-vs-plain-code distinction applies. Where more
 * than one task matches a line, the most complete (then most recently
 * updated) one wins.
 *
 * @returns Map<line code, { task, scoped }>
 */
export const matchSwitchTasks = (lines, tasks) => {
  const aliases = lineAliases(lines);
  const byCode = new Map();

  (Array.isArray(tasks) ? tasks : []).forEach((task) => {
    const hit = resolveWbsCode(task?.taskCode, null, aliases);
    if (!hit) return;

    const existing = byCode.get(hit.code);
    if (!existing) {
      byCode.set(hit.code, { task, scoped: hit.scoped });
      return;
    }
    const incoming = num(task?.progress) ?? 0;
    const current = num(existing.task?.progress) ?? 0;
    if (
      incoming > current ||
      (incoming === current &&
        new Date(task?.updatedAt ?? 0).getTime() >
          new Date(existing.task?.updatedAt ?? 0).getTime())
    ) {
      byCode.set(hit.code, { task, scoped: hit.scoped });
    }
  });

  return byCode;
};

// ── schedule ─────────────────────────────────────────────────────────────────

const MANHOUR_UNIT = /man\s*-?\s*hours?/i;

/** Hours behind a line: the task's own figure, else the estimate quantity
 * when the line is measured in man-hours. */
const lineHours = (line, task) => {
  const fromTask = num(task?.manhours) || num(task?.estimatedTime);
  if (fromTask > 0) return fromTask;
  return MANHOUR_UNIT.test(line.unit || "") && line.eq > 0 ? line.eq : null;
};

/** The task's work window: what actually happened where it's recorded,
 * otherwise the revised, then the original plan. A finished task ends on the
 * day its work was done. `start` is null when the task has no usable one. */
const workWindowOf = (task, fired) => {
  const start =
    isoDateOnly(task?.actualStart) ??
    isoDateOnly(task?.revisedStart) ??
    isoDateOnly(task?.startDate);
  const end = fired
    ? workDoneDateOf(task)
    : isoDateOnly(task?.revisedEnd) ?? isoDateOnly(task?.endDate);
  return { start: start && end && start <= end ? start : null, end };
};

/** [start, end] cut into pieces of `packetDays`, each with the share of the
 * window it covers (`from`/`to`, 0..1). */
const splitWindow = (start, end, packetDays) => {
  const total = diffDays(start, end);
  if (total <= 0) return [{ start, end, from: 0, to: 1 }];
  const count = Math.ceil(total / packetDays);
  return Array.from({ length: count }, (_, k) => {
    const fromDay = k * packetDays;
    const toDay = Math.min(total, fromDay + packetDays);
    return {
      start: addDays(start, fromDay),
      end: addDays(start, toDay),
      from: fromDay / total,
      to: toDay / total,
    };
  });
};

/**
 * A line's packets: several for a man-hour line with a dated task, otherwise
 * one covering the whole line. Each carries its share of cost and client
 * value, and - once its work is done - its switch / client-pay / vendor-pay
 * dates.
 */
const buildPackets = ({ task, fired, payoutType, cost, value, resubmittals, policy, todayIso }) => {
  const window = task ? workWindowOf(task, fired) : { start: null, end: null };
  const progress = fired ? 100 : (num(task?.progress) ?? 0);
  const pieces =
    payoutType === "manhour" && window.start
      ? splitWindow(window.start, window.end, policy.packetDays)
      : [{ start: window.start, end: window.end, from: 0, to: 1 }];

  return pieces.map((piece, k) => {
    const isLast = k === pieces.length - 1;
    // A single-piece line is done only when its task is; a packet is done
    // once the task's progress has reached the end of it.
    const done = fired || (pieces.length > 1 && progress >= piece.to * 100);
    const workDoneDate = !done
      ? null
      : fired && isLast
        ? window.end
        : earlierOf(piece.end, todayIso);
    const approvalDays = policy.approvalDays * (1 + (isLast ? resubmittals : 0));
    const switchDate = workDoneDate ? addDays(workDoneDate, approvalDays) : null;
    const clientPayDate = switchDate ? addDays(switchDate, policy.clientLagDays) : null;
    const vendorPayDate = clientPayDate
      ? addDays(clientPayDate, policy.vendorLagDays[payoutType] ?? 0)
      : null;
    const share = piece.to - piece.from;

    return {
      workStart: piece.start,
      workEnd: piece.end,
      cost: cost * share,
      value: value * share,
      workDoneDate,
      switchDate,
      clientPayDate,
      vendorPayDate,
    };
  });
};

/**
 * One row per dashboard line: its client value vs cost, its packets, and the
 * line-level dates (those of its last packet, once every packet's work is
 * done). `matched` is matchSwitchTasks()'s result.
 */
export const computeSwitchSchedule = (lines, matched, policy, now = new Date()) => {
  const todayIso = isoDateOnly(now);

  return lines.map((line) => {
    const hit = matched?.get(line.code);
    const task = hit?.task ?? null;
    const fired = task ? isSwitchFired(task) : false;
    const category = categoryOf(line);
    const payoutType = category?.payoutType || "material";
    const cost = estCost(line) ?? 0;
    const value = cost * (1 + (policy.markupPct[payoutType] ?? 0) / 100);
    const resubmittals = policy.resubmittals[line.code] ?? 0;
    const window = task ? workWindowOf(task, fired) : { start: null, end: null };

    const packets = buildPackets({
      task,
      fired,
      payoutType,
      cost,
      value,
      resubmittals,
      policy,
      todayIso,
    });
    const doneCount = packets.filter((packet) => packet.workDoneDate).length;
    const last = doneCount === packets.length ? packets[packets.length - 1] : null;

    return {
      code: line.code,
      displayCode: line.displayCode,
      desc: line.desc,
      cost,
      value,
      payoutType,
      categoryKey: catKeyOf(line),
      categoryName: category?.name ?? "",
      resubmittals,
      hours: lineHours(line, task),
      scoped: hit?.scoped ?? false,
      progress: task ? (num(task.progress) ?? (fired ? 100 : 0)) : null,
      workStart: window.start,
      workEnd: window.end,
      packets,
      doneCount,
      workDoneDate: last?.workDoneDate ?? null,
      switchDate: last?.switchDate ?? null,
      firstSwitchDate: packets[0].switchDate,
      clientPayDate: last?.clientPayDate ?? null,
      vendorPayDate: last?.vendorPayDate ?? null,
      // >= 0 by construction: vendorPayDate is always clientPayDate + a
      // non-negative lag (normalizeSwitchPolicy clamps it).
      cashHeldDays:
        last?.clientPayDate && last?.vendorPayDate
          ? diffDays(last.clientPayDate, last.vendorPayDate)
          : null,
    };
  });
};

/**
 * A believable demo schedule, deterministic from the estimate lines and
 * "now" - no plant, no tasks. Uses the exact same per-category windows the
 * demo ledger entries use (ledger.js's SAMPLE_WINDOWS), so both tell the same
 * story: a category whose window has fully passed is complete, one still open
 * is part-way through (so its man-hour packets are partly switched).
 */
export const buildDemoSwitchSchedule = (lines, now = new Date()) => {
  const todayIso = isoDateOnly(now);
  const dateAt = (offset, day) =>
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, day))
      .toISOString()
      .slice(0, 10);

  const matched = new Map();
  lines.forEach((line) => {
    const window = SAMPLE_WINDOWS[catKeyOf(line)];
    if (!window) return;
    const start = dateAt(window[0], 1);
    const end = dateAt(window[1], 15);
    const finished = end <= todayIso;
    const elapsed = diffDays(start, todayIso) / Math.max(1, diffDays(start, end));
    matched.set(line.code, {
      task: {
        progress: finished ? 100 : Math.max(0, Math.min(99, Math.round(elapsed * 100))),
        status: finished ? "Completed" : "Progress",
        startDate: start,
        actualStart: start,
        endDate: end,
        actualEnd: finished ? end : null,
        updatedAt: finished ? end : todayIso,
      },
      scoped: false,
    });
  });

  return computeSwitchSchedule(lines, matched, DEFAULT_SWITCH_POLICY, now);
};

/**
 * The one thing the switch model is meant to prevent: a line paid out before
 * any of its switches has fired. Needs a real Paid amount (from POs, see
 * procurementLink.js) on the line - so this only ever flags real data, never
 * demo. Category totals (invoices) can't be checked this way since they
 * aren't per line. A switch still waiting on client approval hasn't fired.
 */
export const switchComplianceFlags = (lines, schedule, now = new Date()) => {
  const todayIso = isoDateOnly(now);
  const byCode = new Map(schedule.map((row) => [row.code, row]));
  return lines
    .filter((line) => {
      if (!(line.pc != null && line.pc > 0)) return false;
      const firstSwitch = byCode.get(line.code)?.firstSwitchDate;
      return !firstSwitch || firstSwitch > todayIso;
    })
    .map((line) => ({
      code: line.code,
      displayCode: line.displayCode,
      desc: line.desc,
      paid: line.pc,
    }));
};
